import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { createProduct, uploadFile } from '../api';


const ProductCreationModal = ({ onClose, canCreateProduct = true, categories = [], taxes = [] }) => {
    const [formData, setFormData] = useState({
        productName: "",
        productImage: "",
        categoryId: "",
        barcodeScope: "global",
        useDefaultTaxes: true,
        taxIds: [],
    });
    const [message, setMessage] = useState("");
    const [messageTone, setMessageTone] = useState("success");
    const [isSaving, setIsSaving] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const categoryOptions = useMemo(
        () => (categories || []).filter((category) => category?.isActive !== false),
        [categories]
    );
    const taxOptions = useMemo(
        () => (taxes || []).filter((tax) => tax?.isActive !== false && tax?.branchActive !== false),
        [taxes]
    );

    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    const handleSave = async (e) => {
        e.preventDefault();
        if (!canCreateProduct) return;
        let response;
        try {
            setIsSaving(true);
            const selectedCategory = categoryOptions.find(
                (category) => String(category.id) === String(formData.categoryId)
            );
            const payload = {
                ...formData,
                categoryId: selectedCategory?.id ?? null,
                category: selectedCategory?.name ?? null,
                taxIds: formData.useDefaultTaxes ? [] : (formData.taxIds || []),
            };
            if (formData.productImage.name) {
                const imageUrl = await uploadFile(formData.productImage);
                if (imageUrl.url) {
                    setFormData({
                        ...formData,
                        productImage: imageUrl.url,
                    });
                    response = await createProduct(branchId, {
                        ...payload,
                        productImage: imageUrl.url,
                    });
                }
            } else {
                response = await createProduct(branchId, payload);
            }
            setMessage(response.message || "Producto creado.");
            setMessageTone("success");
            setFormData({ productName: "", productImage: "", categoryId: "", barcodeScope: "global", useDefaultTaxes: true, taxIds: [] });
            setImagePreview("");
            onClose(false);
            window.location.reload();
        } catch (error) {
            setMessage(error.message || "No se pudo crear el producto.");
            setMessageTone("error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
            onClick={() => onClose(false)}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-gray-100 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Catálogo</p>
                        <h2 className="text-lg font-semibold text-gray-900">Crear producto</h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Configura el nombre, categoría, alcance de barcode e imagen base.
                        </p>
                    </div>
                    <button
                        onClick={() => onClose(false)}
                        className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                    >
                        ✕
                    </button>
                </div>

                <form
                    className="p-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6"
                    onSubmit={handleSave}
                >
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                            <div className="text-sm font-semibold text-gray-700 mb-3">Imagen del producto</div>
                            <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                <img
                                    src={imagePreview || (typeof formData.productImage === "string" ? formData.productImage : "") || "https://placehold.co/400"}
                                    alt="Producto"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="mt-3 flex gap-2">
                                <label className="flex-1 cursor-pointer">
                                    <input
                                        type="file"
                                        id="productImage"
                                        name="productImage"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] || null;
                                            setFormData((prev) => ({
                                                ...prev,
                                                productImage: file || "",
                                            }));
                                            setImagePreview(file ? URL.createObjectURL(file) : "");
                                        }}
                                        accept="image/*"
                                        className="hidden"
                                        disabled={!canCreateProduct || isSaving}
                                    />
                                    <span className="block text-center text-sm font-medium px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                                        Subir imagen
                                    </span>
                                </label>
                                {imagePreview && (
                                    <button
                                        type="button"
                                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:text-gray-800"
                                        onClick={() => {
                                            setImagePreview("");
                                            setFormData((prev) => ({ ...prev, productImage: "" }));
                                        }}
                                        disabled={isSaving}
                                    >
                                        Quitar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                <input
                                    type="text"
                                    id="productName"
                                    name="productName"
                                    value={formData.productName}
                                    onChange={handleInputChange}
                                    required
                                    autoComplete="off"
                                    className="mt-2 border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-200"
                                    disabled={!canCreateProduct || isSaving}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Categoría</label>
                                <select
                                    id="category"
                                    name="categoryId"
                                    required
                                    value={formData.categoryId}
                                    onChange={handleInputChange}
                                    className="mt-2 border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-200"
                                    disabled={!canCreateProduct || isSaving}
                                >
                                    <option value="" disabled>Selecciona una categoría</option>
                                    {categoryOptions.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-gray-600 uppercase">Alcance del código de barras</label>
                            <select
                                id="barcodeScope"
                                name="barcodeScope"
                                value={formData.barcodeScope}
                                onChange={handleInputChange}
                                className="mt-2 border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-200"
                                disabled={!canCreateProduct || isSaving}
                            >
                                <option value="global">Global</option>
                                <option value="per_variety">Por variedad</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-2">
                                Global comparte el código entre todas las variedades. Por variedad permite códigos únicos por tipo.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-semibold text-gray-600 uppercase">Impuestos</label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={formData.useDefaultTaxes}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            useDefaultTaxes: e.target.checked,
                                        }))
                                    }
                                    disabled={!canCreateProduct || isSaving}
                                />
                                Usar impuestos por defecto (IVA)
                            </label>
                            {!formData.useDefaultTaxes && (
                                <div className="flex flex-wrap gap-3">
                                    {taxOptions.length === 0 && (
                                        <p className="text-xs text-gray-500">No hay impuestos activos en esta sucursal.</p>
                                    )}
                                    {taxOptions.map((tax) => (
                                        <label key={tax.id} className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={(formData.taxIds || []).includes(tax.id)}
                                                onChange={(e) => {
                                                    const next = new Set(formData.taxIds || []);
                                                    if (e.target.checked) {
                                                        next.add(tax.id);
                                                    } else {
                                                        next.delete(tax.id);
                                                    }
                                                    setFormData((prev) => ({ ...prev, taxIds: Array.from(next) }));
                                                }}
                                                disabled={!canCreateProduct || isSaving}
                                            />
                                            {tax.name} ({Number(tax.rate || 0)}%)
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {message && (
                            <div
                                className={`rounded-lg px-3 py-2 text-sm ${
                                    messageTone === "error"
                                        ? "bg-red-50 text-red-600 border border-red-200"
                                        : "bg-green-50 text-green-700 border border-green-200"
                                }`}
                            >
                                {message}
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-2 flex items-center justify-between border-t border-gray-100 pt-4">
                        <div className="text-xs text-gray-500">
                            Luego podrás agregar tipos y precios desde el catálogo.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => onClose(false)}
                                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-gray-900"
                                disabled={isSaving}
                            >
                                Cancelar
                            </button>
                            {canCreateProduct && (
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                                    disabled={isSaving || !formData.productName.trim() || !formData.categoryId}
                                >
                                    Guardar
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ProductCreationModal;
